import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Database, ref, onValue } from '@angular/fire/database';
import { firstValueFrom, Observable, combineLatest, distinctUntilChanged, of, Subscription } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { Menu } from '../models/menu.model';
import { CateringMenuCategory } from '../models/cateringMenuCategory.model';
import { Product } from '../models/product.model';
import { CateringAddonGroup } from '../models/cateringAddonGroup.model';
import { dietaryTag } from '../models/dietaryTag.model';
import { ecoTag } from '../models/ecoTag.model';
import { category } from '../models/category.model';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { UnitService } from './unit.service';
import { CartItem } from '../models/cartItem.model';
import { CampaignDetail } from '../models/campaignDetail.model';
import { ServingDeviation } from '../models/serviceSchedule.model';

export type HomeDatePickerMode = 'weekday' | 'fixed';

export interface HomeDatePickerConstraints {
  mode: HomeDatePickerMode;
  disabledDays: number[];
  disabledDates: Date[];
  minDate: Date;
  maxDate: Date | null;
  allowedDates: Date[];
}

@Injectable({ providedIn: 'root' })
export class MenuService {
  private db = inject(Database);

  private _menuSignal = signal<Menu | null>(null);


  filteredMenuItems = signal<Menu | null>(null);

  private searchQuery = signal<string>('');

  /** Menu pages (home/campaign): open the filters drawer (mobile/tablet). */
  readonly menuFiltersDrawerOpen = signal<boolean>(false);

  readonly menu = computed(() => this._menuSignal());
  private unitService = inject(UnitService);
  private readonly routeName$ = toObservable(this.unitService.routeName).pipe(
    distinctUntilChanged()
  );
  private _campaignMenuSignal = signal<Menu | null>(null);
  readonly campaignMenu = computed(() => this._campaignMenuSignal());
  /** Incrementally cached addon groups (keyed by Firebase group id). */
  private _addonGroupsByKey = signal<Record<string, CateringAddonGroup>>({});
  readonly addonGroupsByKey = computed(
    () => new Map(Object.entries(this._addonGroupsByKey()))
  );
  private addonGroupListeners = new Map<string, () => void>();
  /** One live subscription per serving-schedule stream, replaced on each refresh. */
  private servingSubscriptions: Record<string, Subscription | undefined> = {};
  filteredCampaignMenuItems = signal<Menu | null>(null);
  private _selectedMenuType = signal<'normal' | 'campaign'>('normal');
  readonly selectedMenuType = computed(() => this._selectedMenuType());
  readonly hasCampaignMenu = computed(() => {
    const menu = this._campaignMenuSignal();
    return !!menu && !!menu.categories?.length;
  });

  readonly campaignData = computed(() => {
    const campaign = this._campaignMenuSignal()?.campaign;
    if (!campaign) return null;

    return {
      name: this._campaignMenuSignal()?.name || '',
      image: campaign.heroImageUrl || '',
      title: campaign.heroImageText || '',
      titleColor: campaign.heroImageTextColor || '',
      buttonText: campaign.campaignButtonText || ''
    };
  });

  setSelectedMenuType(type: 'normal' | 'campaign'): void {
    this._selectedMenuType.set(type);
  }

  findProductInMenu(menu: Menu | null, productKey: string): Product | undefined {
    if (!menu?.categories) return undefined;

    for (const category of menu.categories) {
      const product = category.products?.find((p) => p.key === productKey);
      if (product) return product;
    }

    return undefined;
  }

  /** Resolve which menu owns a product (regular vs campaign), respecting menu preference. */
  resolveProductMenuContext(productKey: string): {
    product: Product;
    menu: Menu;
    menuType: 'normal' | 'campaign';
  } | null {
    const regularMenu = this.menu();
    const campaignMenu = this.campaignMenu();
    const regularProduct = this.findProductInMenu(regularMenu, productKey);
    const campaignProduct = this.findProductInMenu(campaignMenu, productKey);

    if (!regularProduct && !campaignProduct) {
      return null;
    }

    const useCampaign =
      !!campaignProduct &&
      (!regularProduct || this.menuPreferenceSignal() !== 'regular');

    return useCampaign
      ? { product: campaignProduct, menu: campaignMenu!, menuType: 'campaign' }
      : { product: regularProduct!, menu: regularMenu!, menuType: 'normal' };
  }

  setSearchQuery(query: string) {
    this.searchQuery.set(query);
  }
  getSearchQuery() {
    return this.searchQuery();
  }

  readonly activeMenu = computed(() => {
    return this._selectedMenuType() === 'campaign'
      ? this._campaignMenuSignal()
      : this._menuSignal();
  });

  private pickupAvailabilityData = signal<any[]>([]);

  pickupAvailabilitySignal = computed(() => this.pickupAvailabilityData());

  refreshPickupAvailability(): void {
    const routeName = this.unitService.routeName();
    if (!routeName) return;

    // Replaces any previous subscription: this is called again on every
    // visit to a unit route, and each one holds an onValue listener.
    this.servingSubscriptions['refreshPickupAvailability']?.unsubscribe();
    this.servingSubscriptions['refreshPickupAvailability'] = this.getLivePickupAvailability(routeName).subscribe((days) => {
      this.pickupAvailabilityData.set(days);
    });
  }

  private deliveryAvailabilityData = signal<any[] | null>(null);

  deliveryAvailabilitySignal = computed(() => this.deliveryAvailabilityData());

  refreshDeliveryAvailability(): void {
    const routeName = this.unitService.routeName();
    if (!routeName) return;

    // Replaces any previous subscription: this is called again on every
    // visit to a unit route, and each one holds an onValue listener.
    this.servingSubscriptions['refreshDeliveryAvailability']?.unsubscribe();
    this.servingSubscriptions['refreshDeliveryAvailability'] = this.getLiveDeliveryAvailability(routeName).subscribe((days) => {
      this.deliveryAvailabilityData.set(days);
    });
  }

  private pickupDeviationsData = signal<ServingDeviation[]>([]);

  pickupDeviationsSignal = computed(() => this.pickupDeviationsData());

  refreshPickupDeviations(): void {
    const routeName = this.unitService.routeName();
    if (!routeName) return;

    // Replaces any previous subscription: this is called again on every
    // visit to a unit route, and each one holds an onValue listener.
    this.servingSubscriptions['refreshPickupDeviations']?.unsubscribe();
    this.servingSubscriptions['refreshPickupDeviations'] = this.getLivePickupDeviations(routeName).subscribe((deviations) => {
      this.pickupDeviationsData.set(deviations);
    });
  }

  private deliveryDeviationsData = signal<ServingDeviation[]>([]);

  deliveryDeviationsSignal = computed(() => this.deliveryDeviationsData());

  refreshDeliveryDeviations(): void {
    const routeName = this.unitService.routeName();
    if (!routeName) return;

    // Replaces any previous subscription: this is called again on every
    // visit to a unit route, and each one holds an onValue listener.
    this.servingSubscriptions['refreshDeliveryDeviations']?.unsubscribe();
    this.servingSubscriptions['refreshDeliveryDeviations'] = this.getLiveDeliveryDeviations(routeName).subscribe((deviations) => {
      this.deliveryDeviationsData.set(deviations);
    });
  }

  /** Normalizes Firebase value to a dense Monday=0 … Sunday=6 week (matches pickup dayIndex). */
  normalizeDeliveryAvailabilityWeek(val: unknown): any[] {
    const week: any[] = Array.from({ length: 7 }, (_, i) => ({
      dayIndex: i,
      enabled: false,
      delivaryTime: [] as string[],
    }));
    if (val == null) {
      return week;
    }
    const rows = Array.isArray(val)
      ? val
      : Object.keys(val as object).map((k) => ({
        dayIndex: Number(k),
        ...(val as any)[k],
      }));
    for (const row of rows) {
      if (
        row &&
        typeof row.dayIndex === 'number' &&
        row.dayIndex >= 0 &&
        row.dayIndex < 7
      ) {
        week[row.dayIndex] = {
          dayIndex: row.dayIndex,
          dayName: row.dayName,
          enabled: row.enabled === true,
          delivaryTime: Array.isArray(row.delivaryTime)
            ? row.delivaryTime.filter((t: unknown) => typeof t === 'string')
            : [],
        };
      }
    }
    return week;
  }
  cutOffTimeSignal = toSignal(this.routeName$.pipe(
    switchMap((routeName) => routeName ? this.getCutOffTimeData$(routeName) : of(null))
  ), {
    initialValue: null,
  });
  productionDaysSignal = toSignal(this.routeName$.pipe(
    switchMap((routeName) => routeName ? this.getProductionDays$(routeName) : of([true, true, true, true, true, true, true]))
  ), {
    initialValue: [true, true, true, true, true, true, true], // Default: all days are production days
  });
  menuPreferenceSignal = toSignal(this.routeName$.pipe(
    switchMap((routeName) => routeName ? this.getMenuPreference$(routeName) : of('campaign' as 'campaign' | 'regular'))
  ), {
    initialValue: 'campaign' as 'campaign' | 'regular'
  });
  showMandatoryPopUpForDateSignal = toSignal(
    this.routeName$.pipe(
      switchMap((routeName) =>
        routeName ? this.getShowMandatoryPopUpForDate$(routeName) : of(false)
      )
    ),
    { initialValue: false }
  );
  headerVisible = signal<boolean>(false);
  private _regularMenuLoadSettled = signal<boolean>(false);
  private _campaignMenuLoadSettled = signal<boolean>(false);
  readonly menusLoadComplete = computed(
    () => this._regularMenuLoadSettled() && this._campaignMenuLoadSettled()
  );


  constructor() {
    effect((onCleanup) => {
      const routeName = this.unitService.routeName();
      if (!routeName) return;

      this._regularMenuLoadSettled.set(false);
      this._campaignMenuLoadSettled.set(false);

      const cleanup = this.initMenuStreams(routeName);
      // effect() ignores a returned function — teardown has to go through
      // onCleanup, otherwise every unit visited leaves its menu listeners
      // (~30 per unit) attached for the rest of the session.
      onCleanup(() => cleanup());
    });

    effect(() => {
      this.menuPreferenceSignal();
      this._menuSignal();
      this._campaignMenuSignal();
      this.searchQuery();
      this.reapplyFilteredMenus();
    });
  }


  searchMenu(query: string) {
    this.setSearchQuery(query);
    const normalized = query.trim().toLowerCase();
    this.searchQuery.set(normalized);
    this.applySearchFilter(normalized);
  }

  private applySearchFilter(query: string) {
    const apply = (menu: Menu | null, type: 'regular' | 'campaign'): Menu | null => {
      if (!menu) return null;
      if (!query) return this.applyDuplicateFilter(menu, type);

      const categories = (menu.categories || [])
        .map((cat) => {
          const matched = (cat.products || []).filter((prod) =>
            [
              prod.name,
              prod.description,
              prod.category?.name,
              ...(prod.dietaryTags?.map((t) => t.name) || []),
              ...(prod.ecoTags?.map((t) => t.name) || []),
            ]
              .filter(Boolean)
              .some((f) => String(f).toLowerCase().includes(query))
          );
          return matched.length ? { ...cat, products: matched } : null;
        })
        .filter(Boolean) as CateringMenuCategory[];

      return this.applyDuplicateFilter({ ...menu, categories }, type);
    };

    this.filteredMenuItems.set(apply(this._menuSignal(), 'regular'));
    this.filteredCampaignMenuItems.set(apply(this._campaignMenuSignal(), 'campaign'));
  }

  /** Re-apply duplicate filtering (and search) when menus or menu preference change. */
  private reapplyFilteredMenus(): void {
    const query = this.searchQuery();
    if (query) {
      this.applySearchFilter(query);
      return;
    }

    const regular = this._menuSignal();
    const campaign = this._campaignMenuSignal();
    this.filteredMenuItems.set(
      regular ? this.applyDuplicateFilter(regular, 'regular') : null
    );
    this.filteredCampaignMenuItems.set(
      campaign ? this.applyDuplicateFilter(campaign, 'campaign') : null
    );
  }

  refreshMenu(routeName: string) {
    this.getLiveActiveMenuWithDetails(routeName).subscribe((data) => {
      this._menuSignal.set(data);
      const filteredMenu = this.applyDuplicateFilter(data, 'regular');
      this.filteredMenuItems.set(filteredMenu);
    });
  }

  getLiveActiveMenuWithDetails(routeName: string): Observable<Menu | null> {
    const activeMenuRef = ref(
      this.db,
      `/Clients/${routeName}/catering/activeMenuId`
    );
    return new Observable<string | null>((observer) =>
      onValue(activeMenuRef, (snapshot) => observer.next(snapshot.val()))
    ).pipe(
      switchMap((activeMenuId) =>
        activeMenuId
          ? this.getLiveMenuDetails(routeName, activeMenuId)
          : of(null)
      )
    );
  }

  private getLiveMenuDetails(
    routeName: string,
    menuId: string
  ): Observable<Menu> {
    const menuRef = ref(
      this.db,
      `/Clients/${routeName}/catering/cateringMenus/${menuId}`
    );
    return new Observable<any>((observer) =>
      onValue(menuRef, (snapshot) => observer.next(snapshot.val()))
    ).pipe(
      switchMap((menuData) => {
        if (!menuData) return of(null);
        return this.getLiveCategoriesByMenuId(routeName, menuId).pipe(
          map((categories) => ({ ...menuData, categories }))
        );
      })
    );
  }

  private getLiveCategoriesByMenuId(
    routeName: string,
    menuId: string
  ): Observable<CateringMenuCategory[]> {
    const categoriesRef = ref(
      this.db,
      `/Clients/${routeName}/catering/categories`
    );
    return new Observable<any>((observer) =>
      onValue(categoriesRef, (snapshot) => observer.next(snapshot.val()))
    ).pipe(
      switchMap((categoriesData) => {
        if (!categoriesData) return of([]);

        const filteredCategories = Object.entries(categoriesData)
          .filter(
            ([_, cat]: any) =>
              cat.menuIds && Object.values(cat.menuIds).includes(menuId)
          )
          .map(([categoryId, category]: any) => ({
            key: categoryId,
            ...category,
          }));

        const categoryStreams = filteredCategories.map((cat) => {
          const productIds = cat.productIds
            ? Object.values(cat.productIds)
            : [];
          return this.getLiveProductsByIds(routeName, productIds).pipe(
            map((products) => ({ ...cat, products }))
          );
        });

        return categoryStreams.length ? combineLatest(categoryStreams) : of([]);
      })
    );
  }

  private getLiveProductsByIds(
    routeName: string,
    productIds: any[]
  ): Observable<Product[]> {
    if (!productIds.length) return of([]);
    const productsRef = ref(this.db, `/Clients/${routeName}/catering/products`);
    return new Observable<any>((observer) => {
      return onValue(productsRef, (snapshot) => {
        const allProducts = snapshot.val() || {};
        const selected = productIds
          .map((id) => ({ id, ...allProducts[id] }))
          .filter((p) => p.id);
        observer.next(selected);
      });
    }).pipe(
      switchMap((products: any[]) => {
        if (!products.length) return of([]);
        const enrichedProducts$ = products.map((product) => {
          const dietaryTagIds = product.dietaryTags
            ? Object.values(product.dietaryTags)
            : [];
          const ecoTagIds = product.ecoTags
            ? Object.values(product.ecoTags)
            : [];
          const categoryId = product.category || null;
          return combineLatest([
            this.getTagsByType(routeName, 'dietaryTags', dietaryTagIds),
            this.getTagsByType(routeName, 'ecoTags', ecoTagIds),
            this.getCategoryById(routeName, categoryId),
          ]).pipe(
            map(([dietaryTags, ecoTags, category]) => ({
              ...product,
              dietaryTags,
              ecoTags,
              category,
            }))
          );
        });
        return enrichedProducts$.length
          ? combineLatest(enrichedProducts$)
          : of([]);
      })
    );
  }

  private getTagsByType(
    routeName: string,
    type: 'dietaryTags' | 'ecoTags',
    tagIds: any[]
  ): Observable<dietaryTag[] | ecoTag[]> {
    if (!tagIds || !tagIds.length) return of([]);
    const tagsRef = ref(
      this.db,
      `/Clients/${routeName}/catering/tagCategories/${type}`
    );
    return new Observable<any>((observer) => {
      return onValue(tagsRef, (snapshot) => {
        const allTags = snapshot.val() || {};
        const selectedTags = tagIds
          .map((id) => ({ id, ...allTags[id] }))
          .filter((tag) => tag.id);
        observer.next(selectedTags);
      });
    });
  }

  private getCategoryById(
    routeName: string,
    categoryId: string | null
  ): Observable<category | null> {
    if (!categoryId) return of(null);
    const categoryRef = ref(
      this.db,
      `/Clients/${routeName}/catering/tagCategories/categories/${categoryId}`
    );
    return new Observable<any>((observer) => {
      return onValue(categoryRef, (snapshot) => {
        const data = snapshot.val();
        observer.next(data ? { id: categoryId, ...data } : null);
      });
    });
  }

  getLiveActiveCampaignMenuWithDetails(
    routeName: string
  ): Observable<Menu | null> {
    const activeRef = ref(
      this.db,
      `/Clients/${routeName}/catering/activeMenuCampaignId`
    );

    return new Observable<string | null>((observer) =>
      onValue(activeRef, (snap) => observer.next(snap.val()))
    ).pipe(
      switchMap((id) =>
        id ? this.getLiveCampaignMenuDetails(routeName, id) : of(null)
      )
    );
  }

  private getLiveCampaignMenuDetails(
    routeName: string,
    menuId: string
  ): Observable<Menu> {
    const menuRef = ref(
      this.db,
      `/Clients/${routeName}/catering/cateringCampaignMenus/${menuId}`
    );
    return new Observable<any>((observer) =>
      onValue(menuRef, (snapshot) => observer.next(snapshot.val()))
    ).pipe(
      switchMap((menuData) => {
        if (!menuData) return of(null);
        return this.getLiveCategoriesByMenuId(routeName, menuId).pipe(
          map((categories) => ({ ...menuData, categories }))
        );
      })
    );
  }

  getLivePickupAvailability(routeName: string): Observable<any[]> {
    const pickupRef = ref(
      this.db,
      `/Clients/${routeName}/catering/configuration/pickupAvailability`
    );
    return new Observable((observer) => {
      return onValue(pickupRef, (snapshot) => {
        const data = snapshot.val() || {};
        const days = Object.keys(data).map((key) => ({
          dayIndex: Number(key),
          ...data[key],
        }));
        observer.next(days);
      });
    });
  }

  getLiveDeliveryAvailability(routeName: string): Observable<any[]> {
    const deliveryRef = ref(
      this.db,
      `/Clients/${routeName}/catering/configuration/delivaryAvailability`
    );
    return new Observable((observer) => {
      return onValue(deliveryRef, (snapshot) => {
        observer.next(this.normalizeDeliveryAvailabilityWeek(snapshot.val()));
      });
    });
  }

  getLivePickupDeviations(routeName: string): Observable<ServingDeviation[]> {
    const deviationsRef = ref(
      this.db,
      `/Clients/${routeName}/catering/configuration/pickupDeviations`
    );
    return new Observable<ServingDeviation[]>((observer) => {
      return onValue(deviationsRef, (snapshot) => {
        observer.next(this.normalizeServingDeviations(snapshot.val()));
      });
    });
  }

  getLiveDeliveryDeviations(routeName: string): Observable<ServingDeviation[]> {
    const deviationsRef = ref(
      this.db,
      `/Clients/${routeName}/catering/configuration/deliveryDeviations`
    );
    return new Observable<ServingDeviation[]>((observer) => {
      return onValue(deviationsRef, (snapshot) => {
        observer.next(this.normalizeServingDeviations(snapshot.val()));
      });
    });
  }

  private normalizeServingDeviations(data: unknown): ServingDeviation[] {
    if (!data) {
      return [];
    }

    const list = Array.isArray(data)
      ? data
      : Object.values(data as Record<string, unknown>);

    return list.map((item: any) => ({
      occasion: item?.occasion ?? '',
      startDate: item?.startDate ? new Date(item.startDate) : null,
      endDate: item?.endDate ? new Date(item.endDate) : null,
    }));
  }

  async preloadPickupDeliveryAvailabilityForRoute(routeName: string): Promise<void> {
    if (!routeName) return;
    const [pickupDays, deliveryDays, pickupDeviations, deliveryDeviations] =
      await Promise.all([
        firstValueFrom(this.getLivePickupAvailability(routeName).pipe(take(1))),
        firstValueFrom(this.getLiveDeliveryAvailability(routeName).pipe(take(1))),
        firstValueFrom(this.getLivePickupDeviations(routeName).pipe(take(1))),
        firstValueFrom(this.getLiveDeliveryDeviations(routeName).pipe(take(1))),
      ]);
    this.pickupAvailabilityData.set(pickupDays);
    this.deliveryAvailabilityData.set(deliveryDays);
    this.pickupDeviationsData.set(pickupDeviations);
    this.deliveryDeviationsData.set(deliveryDeviations);
  }

  getCutOffTimeData$(routeName: string): Observable<any> {
    return new Observable<any>((observer) => {
      return onValue(
        ref(
          this.db,
          `/Clients/${routeName}/catering/configuration/cutoffTime`
        ),
        (snapshot) => {
          const data = snapshot.val() || {};
          observer.next(data);
        }
      );
    });
  }

  getProductionDays$(routeName: string): Observable<boolean[]> {
    return new Observable<boolean[]>((observer) => {
      return onValue(
        ref(
          this.db,
          `/Clients/${routeName}/catering/configuration/productionDays`
        ),
        (snapshot) => {
          const data = snapshot.val();
          // Default: all days are production days (Monday-Sunday)
          if (!Array.isArray(data) || data.length !== 7) {
            observer.next([true, true, true, true, true, true, true]);
          } else {
            observer.next(data);
          }
        }
      );
    });
  }

  getMenuPreference$(routeName: string): Observable<'campaign' | 'regular'> {
    return new Observable<'campaign' | 'regular'>((observer) => {
      return onValue(
        ref(
          this.db,
          `/Clients/${routeName}/catering/configuration/menuPreference`
        ),
        (snapshot) => {
          const preference = snapshot.val();
          // Default to 'campaign' if no preference is set
          observer.next(preference === 'regular' ? 'regular' : 'campaign');
        }
      );
    });
  }

  private findDuplicateProductIds(regularMenu: Menu | null, campaignMenu: Menu | null): Set<string> {
    const duplicateIds = new Set<string>();

    if (!regularMenu || !campaignMenu) return duplicateIds;

    const regularProductIds = new Set<string>();
    const campaignProductIds = new Set<string>();

    if (regularMenu.categories) {
      for (let i = 0; i < regularMenu.categories.length; i++) {
        const products = regularMenu.categories[i]?.products;
        if (products) {
          for (let j = 0; j < products.length; j++) {
            if (products[j]?.key) {
              regularProductIds.add(products[j].key);
            }
          }
        }
      }
    }

    if (campaignMenu.categories) {
      for (let i = 0; i < campaignMenu.categories.length; i++) {
        const products = campaignMenu.categories[i]?.products;
        if (products) {
          for (let j = 0; j < products.length; j++) {
            if (products[j]?.key) {
              campaignProductIds.add(products[j].key);
            }
          }
        }
      }
    }

    for (const id of regularProductIds) {
      if (campaignProductIds.has(id)) {
        duplicateIds.add(id);
      }
    }

    return duplicateIds;
  }

  private applyDuplicateFilter(menu: Menu | null, menuType: 'regular' | 'campaign'): Menu | null {
    if (!menu) return null;

    const regularMenu = menuType === 'regular' ? menu : this._menuSignal();
    const campaignMenu = menuType === 'campaign' ? menu : this._campaignMenuSignal();
    const preference = this.menuPreferenceSignal();

    // If no preference or no other menu, return menu as is
    if (!preference || (menuType === 'regular' && !campaignMenu) || (menuType === 'campaign' && !regularMenu)) {
      return menu;
    }

    // Find duplicate product IDs
    const duplicateIds = this.findDuplicateProductIds(regularMenu, campaignMenu);

    // If no duplicates, return menu as is
    if (duplicateIds.size === 0) {
      return menu;
    }

    // Remove duplicates from non-preferred menu
    // If preference is 'campaign' and this is regular menu, remove duplicates
    // If preference is 'regular' and this is campaign menu, remove duplicates
    const shouldRemoveDuplicates =
      (preference === 'campaign' && menuType === 'regular') ||
      (preference === 'regular' && menuType === 'campaign');

    if (!shouldRemoveDuplicates) {
      return menu;
    }

    // Filter out duplicate products
    if (!menu.categories) return menu;

    const filteredCategories = menu.categories.map(cat => {
      if (!cat.products?.length) return cat;

      const filteredProducts = cat.products.filter(prod =>
        !prod.key || !duplicateIds.has(prod.key)
      );

      return {
        ...cat,
        products: filteredProducts
      };
    }).filter(cat => cat.products && cat.products.length > 0);

    return {
      ...menu,
      categories: filteredCategories
    };
  }

  private getCampaignProductIds(): string[] {
    const campaignMenu = this._campaignMenuSignal();
    if (!campaignMenu?.categories?.length) {
      return [];
    }
    return campaignMenu.categories.flatMap(
      (cat) => (cat.productIds || []) as string[]
    );
  }

  private normalizeToStartOfDay(date: Date): Date {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    return normalizedDate;
  }

  private isCampaignDateRangePassed(menu: Menu | null): boolean {
    const campaignContext = menu?.campaign ?? menu?.event;
    if (!campaignContext?.startDate || !campaignContext?.endDate) {
      return false;
    }

    const today = this.normalizeToStartOfDay(new Date()).getTime();
    const start = this.normalizeToStartOfDay(new Date(campaignContext.startDate)).getTime();
    const end = this.normalizeToStartOfDay(new Date(campaignContext.endDate)).getTime();

    return start < today && end < today;
  }

  readonly hasFuturePickupDate = computed(() => {
    const menu = this._campaignMenuSignal();
    if (menu?.campaign?.isdatesFixed === false) return true;
    if (!menu?.campaign?.pickup || !Array.isArray(menu.campaign.pickup)) return false;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return menu.campaign.pickup.some((pickup: { date: string | number | Date }) => {
      if (!pickup?.date) return false;
      const d = new Date(pickup.date);
      d.setHours(0, 0, 0, 0);
      return d >= now;
    });
  });

  private getNormalProductIds(): string[] {
    const menu = this._menuSignal();
    if (!menu?.categories?.length) return [];
    return menu.categories.flatMap(cat => cat.productIds ?? []);
  }

  private effectiveCampaignProductIdsSet = computed(() => {
    let ids = this.getCampaignProductIds();
    if (this.menuPreferenceSignal() === 'regular') {
      const normalIds = this.getNormalProductIds();
      ids = this.filterOutNormalProducts(ids, normalIds);
    }
    return new Set(ids);
  });

  private filterOutNormalProducts(
    campaignIds: string[],
    normalIds: string[]
  ): string[] {
    const normalSet = new Set(normalIds);
    return campaignIds.filter(id => !normalSet.has(id));
  }

  hasCampaignItems(cartItems: CartItem[]): boolean {
    if (!cartItems.length) return false;
    const set = this.effectiveCampaignProductIdsSet();
    if (set.size === 0) return false;
    return cartItems.some(item => set.has(item.product.key));
  }
  showHeader(): void {
    this.headerVisible.set(true);
  }
  hideHeader(): void {
    this.headerVisible.set(false);
  }

  getNonCampaignItems(cartItems: CartItem[]): CartItem[] {
    const set = this.effectiveCampaignProductIdsSet();
    if (set.size === 0) return cartItems;
    return cartItems.filter(item => !set.has(item.product.key));
  }

  getCampaignItems(cartItems: CartItem[]): CartItem[] {
    const set = this.effectiveCampaignProductIdsSet();
    if (set.size === 0) return [];
    return cartItems.filter(item => set.has(item.product.key));
  }

  splitCampaignAndNonCampaignItems(cartItems: CartItem[]): {
    campaignItems: CartItem[];
    nonCampaignItems: CartItem[];
  } {
    const set = this.effectiveCampaignProductIdsSet();
    const campaignItems: CartItem[] = [];
    const nonCampaignItems: CartItem[] = [];
    if (set.size === 0) {
      return { campaignItems: [], nonCampaignItems: cartItems };
    }
    for (const item of cartItems) {
      if (set.has(item.product.key)) campaignItems.push(item);
      else nonCampaignItems.push(item);
    }
    return { campaignItems, nonCampaignItems };
  }
  private initMenuStreams(routeName: string): () => void {
    const subs: Subscription[] = [];

    subs.push(
      this.getLiveActiveMenuWithDetails(routeName).pipe(
        tap(() => {
          if (!this._regularMenuLoadSettled()) {
            this._regularMenuLoadSettled.set(true);
          }
        })
      ).subscribe((data) => {
        this._menuSignal.set(data ?? null);
        this.reapplyFilteredMenus();
      })
    );

    subs.push(
      this.getLiveActiveCampaignMenuWithDetails(routeName).pipe(
        tap(() => {
          if (!this._campaignMenuLoadSettled()) {
            this._campaignMenuLoadSettled.set(true);
          }
        })
      ).subscribe((data) => {
        const campaignMenu = this.isCampaignDateRangePassed(data) ? null : data;
        this._campaignMenuSignal.set(campaignMenu ?? null);
        this.reapplyFilteredMenus();
      })
    );

    return () => {
      subs.forEach((s) => s.unsubscribe());
      this.clearAddonCaches();
    };
  }

  /**
   * Fetch only addon groups attached to a product. Already-cached groups are reused;
   * only missing group ids get a new Firebase listener.
   */
  ensureAddonGroupsForProduct(groupIds: string[] | undefined): void {
    const routeName = this.unitService.routeName();
    if (!routeName) return;

    const normalized = this.normalizeStringIdList(groupIds);
    for (const groupId of normalized) {
      this.ensureAddonGroup(routeName, groupId);
    }
  }

  private ensureAddonGroup(routeName: string, groupId: string): void {
    if (this.addonGroupListeners.has(groupId)) {
      return;
    }

    const groupRef = ref(
      this.db,
      `/Clients/${routeName}/catering/addonGroups/${groupId}`
    );
    const unsubscribe = onValue(groupRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        return;
      }
      const group = this.mapAddonGroupFromData(groupId, data);
      this._addonGroupsByKey.update((cache) => ({
        ...cache,
        [groupId]: group,
      }));
    });
    this.addonGroupListeners.set(groupId, unsubscribe);
  }

  private mapAddonGroupFromData(
    key: string,
    group: Record<string, unknown>
  ): CateringAddonGroup {
    const availability = group['availability'] as
      | { pickup?: boolean; delivery?: boolean }
      | undefined;
    return {
      key,
      id: key,
      name: (group['name'] as string) ?? '',
      description: (group['description'] as string) ?? '',
      type: group['type'] === 'multiple' ? 'multiple' : 'single',
      options: Array.isArray(group['options'])
        ? (group['options'] as CateringAddonGroup['options'])
        : [],
      availability: {
        pickup: availability?.pickup !== false,
        delivery: availability?.delivery !== false,
      },
      minSelections: group['minSelections'] as number | undefined,
      maxSelections: group['maxSelections'] as number | null | undefined,
      preselectMode:
        group['preselectMode'] === 'specific' ? 'specific' : 'first',
      preselectedOptionId: (group['preselectedOptionId'] as string) ?? null,
      associatedProductIds: this.normalizeStringIdList(
        group['associatedProductIds']
      ),
    };
  }

  private clearAddonCaches(): void {
    for (const unsubscribe of this.addonGroupListeners.values()) {
      unsubscribe();
    }
    this.addonGroupListeners.clear();

    this._addonGroupsByKey.set({});
  }

  private normalizeStringIdList(ids: unknown): string[] {
    const list = Array.isArray(ids)
      ? ids
      : ids && typeof ids === 'object'
        ? Object.values(ids as Record<string, unknown>)
        : [];
    return [
      ...new Set(
        list
          .map((id) => (typeof id === 'string' ? id.trim() : ''))
          .filter((id): id is string => !!id)
      ),
    ];
  }

  getCommentOnOrder$(): Observable<boolean> {
    const routeName = this.unitService.routeName();
    if (!routeName) throw new Error('Route name not set');
    return new Observable<boolean>(observer => {
      const refPath = `/Clients/${routeName}/catering/configuration/commentFieldOnOrder`;
      return onValue(ref(this.db, refPath), snapshot => {
        observer.next(!!snapshot.val());
      });
    });
  }

  getShowMandatoryPopUpForDate$(routeName: string): Observable<boolean> {
    return new Observable<boolean>((observer) =>
      onValue(
        ref(
          this.db,
          `/Clients/${routeName}/catering/configuration/showMandatoryPopUpForDate`
        ),
        (snapshot) => observer.next(!!snapshot.val())
      )
    );
  }
  /**
   * Firebase week for `servingMethod` when that method is enabled at the unit; otherwise null.
   * Matches checkout `getScheduleDaysForCurrentMode` rules.
   */
  getAvailabilityDaysForServingMethod(
    servingMethod: 'pickup' | 'delivery'
  ): any[] | null {

    const pickupEnabled = this.unitService.pickupServingEnabled();
    // Guest invoice orders run on the delivery schedule too.
    const deliveryEnabled = this.unitService.deliveryOrderingEnabled();

    // ALWAYS read signals
    const pickup = this.pickupAvailabilitySignal();
    const delivery = this.deliveryAvailabilitySignal();

    if (servingMethod === 'delivery') {
      if (!deliveryEnabled) {
        return null;
      }

      if (Array.isArray(delivery) && delivery.length >= 7) {
        return delivery;
      }

      return null;
    }

    if (!pickupEnabled) {
      return null;
    }

    if (Array.isArray(pickup) && pickup.length > 0) {
      return pickup;
    }

    return null;
  }

  getMinPickupDateForProduct(product: Product, servingMethod: 'pickup' | 'delivery' = 'pickup'): Date {
    const cutoffData = this.cutOffTimeSignal();
    const productionDays = this.productionDaysSignal() || [true, true, true, true, true, true, true];
    const maxLeadDays = product?.minimumOrderLeadDays || 0;

    let startDate = new Date();

    if (cutoffData?.cutOffTimeEnabled && cutoffData?.cutoffTime) {
      const cutoffDate = new Date(cutoffData.cutoffTime);

      if (!isNaN(cutoffDate.getTime())) {
        const cutoffHour = cutoffDate.getHours();
        const cutoffMinute = cutoffDate.getMinutes();
        const now = new Date();

        const isBeforeCutoff =
          now.getHours() < cutoffHour ||
          (now.getHours() === cutoffHour && now.getMinutes() < cutoffMinute);

        if (!isBeforeCutoff) {
          startDate.setDate(startDate.getDate() + 1);
        }
      }
    } else {
      startDate.setDate(startDate.getDate() + 1);
    }

    const deviations =
      servingMethod === 'delivery'
        ? this.deliveryDeviationsSignal()
        : this.pickupDeviationsSignal();
    const deviationDateKeys = this.getServingDeviationDateKeys(servingMethod);
    const date = this.addProductionDays(
      startDate,
      maxLeadDays,
      productionDays,
      deviationDateKeys
    );
    const days = this.getAvailabilityDaysForServingMethod(servingMethod);

    return this.getNextEnabledDate(date, days ?? [], deviations);
  }

  formatDisplayDate(date: Date): string {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  /** Compact day + month for inline ranges, e.g. "31 may". */
  formatDisplayDateRangePart(date: Date): string {
    const d = new Date(date);
    const day = d.getDate();
    const month = d.toLocaleDateString('en-US', { month: 'short' }).toLowerCase();
    return `${day} ${month}`;
  }

  private isFlexibleCampaignSchedulable(campaign: CampaignDetail): boolean {
    return campaign.status === 'active' && campaign.isdatesFixed !== true && !!campaign.endDate;
  }

  resolveCampaignFlexibleEarliestPickup(
    campaign: CampaignDetail,
    product: Product,
    servingMethod: 'pickup' | 'delivery' = 'pickup'
  ): Date {
    let earliest = this.normalizeToStartOfDay(
      this.getMinPickupDateForProduct(product, servingMethod)
    );

    if (campaign.startDate) {
      const campaignStart = this.normalizeToStartOfDay(new Date(campaign.startDate));
      if (earliest.getTime() < campaignStart.getTime()) {
        earliest = campaignStart;
      }
    }

    return earliest;
  }

  private isServingDateSelectable(
    date: Date,
    deviationDateKeys: Set<number>,
    days: any[] | null
  ): boolean {
    if (this.isDateInDeviationKeys(date, deviationDateKeys)) {
      return false;
    }

    const hasEnabledDays =
      days && days.length > 0 && days.some((d: any) => d?.enabled);
    if (!hasEnabledDays) {
      return true;
    }

    const customDayIndex = this.jsDayIndexToCustomIndex(date.getDay());
    return !!days[customDayIndex]?.enabled;
  }

  private computeCampaignFlexibleSchedule(
    campaign: CampaignDetail,
    product: Product,
    servingMethod: 'pickup' | 'delivery',
    earliestPickupDate?: Date
  ): { allowedDates: Date[] } | null {
    if (!this.isFlexibleCampaignSchedulable(campaign)) {
      return null;
    }

    const windowStart = this.normalizeToStartOfDay(
      earliestPickupDate ??
        this.resolveCampaignFlexibleEarliestPickup(campaign, product, servingMethod)
    );
    const windowEnd = this.normalizeToStartOfDay(new Date(campaign.endDate!));

    if (windowStart.getTime() > windowEnd.getTime()) {
      return null;
    }

    const deviationDateKeys = this.getServingDeviationDateKeys(servingMethod);
    const days = this.getAvailabilityDaysForServingMethod(servingMethod);
    const allowedDates: Date[] = [];
    const current = new Date(windowStart);

    while (current.getTime() <= windowEnd.getTime()) {
      const day = this.startOfDay(new Date(current));

      if (this.isServingDateSelectable(day, deviationDateKeys, days)) {
        allowedDates.push(new Date(day));
      }

      current.setDate(current.getDate() + 1);
    }

    return { allowedDates };
  }

  /** Compact range label, e.g. "20-30 jun" or "31 may - 2 jun". */
  formatDateRangeLabel(start: Date, end: Date): string {
    const rangeStart = this.normalizeToStartOfDay(start);
    const rangeEnd = this.normalizeToStartOfDay(end);

    if (rangeStart.getTime() > rangeEnd.getTime()) {
      return '';
    }

    if (rangeStart.getTime() === rangeEnd.getTime()) {
      return this.formatDisplayDateRangePart(rangeStart);
    }

    const startMonth = rangeStart
      .toLocaleDateString('en-US', { month: 'short' })
      .toLowerCase();
    const endMonth = rangeEnd
      .toLocaleDateString('en-US', { month: 'short' })
      .toLowerCase();

    if (startMonth === endMonth) {
      return `${rangeStart.getDate()}-${rangeEnd.getDate()} ${startMonth}`;
    }

    return `${this.formatDisplayDateRangePart(rangeStart)} - ${this.formatDisplayDateRangePart(rangeEnd)}`;
  }

  /** Allowed pickup/delivery days inside a flexible campaign window. */
  getCampaignFlexibleAllowedDates(
    campaign: CampaignDetail,
    product: Product,
    servingMethod: 'pickup' | 'delivery' = 'pickup',
    earliestPickupDate?: Date
  ): Date[] {
    return (
      this.computeCampaignFlexibleSchedule(
        campaign,
        product,
        servingMethod,
        earliestPickupDate
      )?.allowedDates ?? []
    );
  }

  hasNoOrderableDates(
    product: Product,
    servingMethod: 'pickup' | 'delivery' = 'pickup'
  ): boolean {
    const menu = this.activeMenu();
    const campaign =
      this.selectedMenuType() === 'campaign' ? menu?.campaign : null;

    if (campaign?.pickup?.length) {
      return this.getFuturePickupDates(campaign.pickup, servingMethod).length === 0;
    }

    if (campaign && this.isFlexibleCampaignSchedulable(campaign)) {
      return this.getCampaignFlexibleAllowedDates(campaign, product, servingMethod)
        .length === 0;
    }

    const fixedContext = this.getActiveFixedDateContext(menu);
    if (fixedContext?.pickup?.length) {
      return this.getFuturePickupDates(fixedContext.pickup, servingMethod).length === 0;
    }

    return false;
  }

  /** Single-line range for active flexible campaigns, e.g. "20-30 jun". */
  getCampaignFlexibleDateRangeLabel(
    campaign: CampaignDetail,
    product: Product,
    servingMethod: 'pickup' | 'delivery' = 'pickup',
    earliestPickupDate?: Date
  ): string {
    if (!this.isFlexibleCampaignSchedulable(campaign)) {
      return '';
    }

    const start = this.normalizeToStartOfDay(
      earliestPickupDate ??
        this.resolveCampaignFlexibleEarliestPickup(campaign, product, servingMethod)
    );
    const end = this.normalizeToStartOfDay(new Date(campaign.endDate!));

    return this.formatDateRangeLabel(start, end);
  }

  addProductionDays(
    startDate: Date,
    productionDaysToAdd: number,
    productionDays: boolean[],
    deviationDateKeys?: Set<number>
  ): Date {
    if (!productionDays || productionDays.length !== 7 || productionDaysToAdd <= 0) {
      return new Date(startDate);
    }

    let currentDate = new Date(startDate);
    let productionDaysCounted = 0;

    while (productionDaysCounted < productionDaysToAdd) {
      const jsDayIndex = currentDate.getDay();
      const customDayIndex = this.jsDayIndexToCustomIndex(jsDayIndex);

      const isWeekdayProductionDay = productionDays[customDayIndex] !== false;
      const isDeviationDay =
        !!deviationDateKeys?.size &&
        this.isDateInDeviationKeys(currentDate, deviationDateKeys);
      const countsAsProductionDay = isWeekdayProductionDay && !isDeviationDay;

      if (countsAsProductionDay) {
        productionDaysCounted++;
      }

      if (productionDaysCounted < productionDaysToAdd) {
        currentDate.setDate(currentDate.getDate() + 1);
      }
    }

    currentDate.setDate(currentDate.getDate() + 1);
    return currentDate;
  }

  getNextEnabledDate(
    selectedDate: Date,
    days: any[],
    deviations: ServingDeviation[]
  ): Date {
    const useDeviations = !!deviations?.length;

    if (!useDeviations) {
      return this.getNextEnabledWeekdayDate(selectedDate, days);
    }

    let result = this.startOfDay(new Date(selectedDate));
    const hasEnabledDays =
      days && days.length > 0 && days.some((d: any) => d?.enabled);

    while (true) {
      result = this.movePastServingDeviationRanges(result, deviations);

      if (!hasEnabledDays) {
        return result;
      }

      const customDayIndex = this.jsDayIndexToCustomIndex(result.getDay());
      if (days[customDayIndex]?.enabled) {
        return result;
      }

      result = this.getNextEnabledWeekdayDate(result, days);
    }
  }

  private getNextEnabledWeekdayDate(selectedDate: Date, days: any[]): Date {
    const hasEnabledDays = days && days.length > 0 && days.some((d: any) => d?.enabled);
    if (!hasEnabledDays) {
      return new Date(selectedDate);
    }

    const customDayIndex = this.jsDayIndexToCustomIndex(selectedDate.getDay());
    if (days[customDayIndex]?.enabled) {
      return new Date(selectedDate);
    }

    for (let i = 1; i <= 7; i++) {
      const nextIndex = (customDayIndex + i) % 7;

      if (days[nextIndex]?.enabled) {
        const newDate = new Date(selectedDate);
        newDate.setDate(newDate.getDate() + i);
        return newDate;
      }
    }

    return new Date(selectedDate);
  }

  jsDayIndexToCustomIndex(jsIndex: number): number {
    return jsIndex === 0 ? 6 : jsIndex - 1;
  }
  customToPrimeDayIndex(customIndex: number): number {
    return (customIndex + 1) % 7;
  }

  getFuturePickupDates(
    data: any[],
    servingMethod: 'pickup' | 'delivery' = 'pickup'
  ): Date[] {
    if (!Array.isArray(data) || !data.length) {
      return [];
    }

    const today = this.startOfDay(new Date());
    const deviationDateKeys = this.getServingDeviationDateKeys(servingMethod);

    return data
      .map((item) => this.parseCampaignLocalDate(item.date))
      .filter((date) => !isNaN(date.getTime()))
      .filter((date) => this.startOfDay(date).getTime() >= today.getTime())
      .filter((date) => !this.isDateInDeviationKeys(this.startOfDay(date), deviationDateKeys))
      .sort((a, b) => a.getTime() - b.getTime());
  }

  private normalizeDate(date: Date): number {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    ).getTime();
  }
  private isDateAvailable(
    dateToCheck: Date,
    schedules: any[]
  ): boolean {
    const target = this.normalizeDate(dateToCheck);

    return schedules.some(schedule =>
      this.normalizeDate(new Date(schedule.date)) === target
    );
  }

  prepareProduct(
    product: any,
    selectedDate: any,
    menuType: string,
    menu?: any,
    servingMethod: 'pickup' | 'delivery' = 'pickup'
  ) {
    if (!selectedDate) return product;
    const selectedTime = this.normalizeDate(new Date(selectedDate));
    if (menuType === 'campaign') {
      const campaign = menu?.campaign;
      const pickupSchedules = campaign?.pickup;
      if (pickupSchedules?.length) {
        const earliestPickupDate =
          this.getFuturePickupDates(pickupSchedules, servingMethod)[0] ?? null;
        return {
          ...product,
          earliestPickupDate,
          isAvailableOnSelectedDate: this.isDateAvailable(
            new Date(selectedDate),
            pickupSchedules
          )
        };
      }

      const earliestPickupDate = this.resolveCampaignFlexibleEarliestPickup(
        campaign,
        product,
        servingMethod
      );
      const schedule = this.computeCampaignFlexibleSchedule(
        campaign,
        product,
        servingMethod,
        earliestPickupDate
      );
      const hasAllowedDates = !!schedule?.allowedDates.length;
      const campaignEnd = this.normalizeToStartOfDay(new Date(campaign.endDate!));

      return {
        ...product,
        earliestPickupDate: hasAllowedDates
          ? schedule!.allowedDates[0]
          : undefined,
        availablePickupDateRange: this.formatDateRangeLabel(
          earliestPickupDate,
          campaignEnd
        ),
        isAvailableOnSelectedDate: hasAllowedDates
          ? schedule!.allowedDates.some(
              (allowed) => this.normalizeDate(allowed) === selectedTime
            )
          : false,
      };
    }
    const fixedHomeContext = this.getActiveFixedDateContext(menu);
    if (fixedHomeContext?.pickup?.length) {
      const earliestPickupDate =
        this.getFuturePickupDates(fixedHomeContext.pickup, servingMethod)[0] ?? null;
      return {
        ...product,
        earliestPickupDate,
        isAvailableOnSelectedDate: this.isDateAvailable(
          new Date(selectedDate),
          fixedHomeContext.pickup
        )
      };
    }

    const earliestPickupDate =
      this.getMinPickupDateForProduct(product, servingMethod);

    if (!earliestPickupDate) return product;

    const productTime = this.normalizeDate(new Date(earliestPickupDate));

    return {
      ...product,
      earliestPickupDate,
      isAvailableOnSelectedDate: productTime <= selectedTime
    };
  }

  getDisabledDaysForServingMethod(servingMethod: 'pickup' | 'delivery'): number[] {
    const days = servingMethod === 'delivery'
      ? this.deliveryAvailabilitySignal()
      : this.pickupAvailabilitySignal();

    if (!days || !Array.isArray(days) || days.length === 0) {
      return [];
    }

    return days
      .filter(day => !day.enabled)
      .map(day => this.customToPrimeDayIndex(day.dayIndex));
  }

  /** Specific calendar dates disabled by admin deviation ranges for the serving method. */
  getDeviationDisabledDatesForServingMethod(
    servingMethod: 'pickup' | 'delivery'
  ): Date[] {
    const deviations =
      servingMethod === 'delivery'
        ? this.deliveryDeviationsSignal()
        : this.pickupDeviationsSignal();

    const disabled: Date[] = [];
    const seen = new Set<number>();

    for (const deviation of deviations) {
      if (!deviation.startDate || !deviation.endDate) {
        continue;
      }

      const start = this.startOfDay(new Date(deviation.startDate));
      const end = this.startOfDay(new Date(deviation.endDate));
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
        continue;
      }

      const current = new Date(start);
      while (current.getTime() <= end.getTime()) {
        const day = this.startOfDay(new Date(current));
        const key = day.getTime();
        if (!seen.has(key)) {
          seen.add(key);
          disabled.push(day);
        }
        current.setDate(current.getDate() + 1);
      }
    }

    return disabled;
  }

  getHomeDatePickerConstraints(
    menuType: 'home' | 'campaign',
    servingMethod: 'pickup' | 'delivery'
  ): HomeDatePickerConstraints {
    const today = this.startOfDay(new Date());

    const context = this.resolveFixedHomeDateContext(menuType);
    if (context) {
      const fixedAllowedDates = this.getFixedPickupDates(context);

      if (fixedAllowedDates.length > 0) {
        const deviationDateKeys = this.getServingDeviationDateKeys(servingMethod);
        const allowedDates = fixedAllowedDates.filter(
          (date) => !this.isDateInDeviationKeys(date, deviationDateKeys)
        );
        const minPickup = this.startOfDay(
          new Date(Math.min(...fixedAllowedDates.map((d) => d.getTime())))
        );
        const maxPickup = this.startOfDay(
          new Date(Math.max(...fixedAllowedDates.map((d) => d.getTime())))
        );

        return {
          mode: 'fixed',
          disabledDays: [],
          disabledDates: this.buildDisabledDatesForFixedRange(
            minPickup,
            maxPickup,
            allowedDates,
            today,
            deviationDateKeys
          ),
          minDate: minPickup,
          maxDate: maxPickup,
          allowedDates,
        };
      }
    }

    return {
      mode: 'weekday',
      disabledDays: this.getDisabledDaysForServingMethod(servingMethod),
      disabledDates: this.getDeviationDisabledDatesForServingMethod(servingMethod),
      minDate: today,
      maxDate: null,
      allowedDates: [],
    };
  }

  isHomeSelectedDateAllowed(
    date: Date,
    menuType: 'home' | 'campaign',
    servingMethod: 'pickup' | 'delivery'
  ): boolean {
    const constraints = this.getHomeDatePickerConstraints(menuType, servingMethod);
    const checkDate = this.startOfDay(date);
    const deviationDateKeys = this.getServingDeviationDateKeys(servingMethod);

    if (checkDate < constraints.minDate) {
      return false;
    }
    if (constraints.maxDate && checkDate > constraints.maxDate) {
      return false;
    }

    if (this.isDateInDeviationKeys(checkDate, deviationDateKeys)) {
      return false;
    }

    if (constraints.mode === 'fixed') {
      return constraints.allowedDates.some(
        (allowed) => this.normalizeDate(allowed) === this.normalizeDate(checkDate)
      );
    }

    return !constraints.disabledDays.includes(checkDate.getDay());
  }

  parseCampaignLocalDate(input: unknown): Date {
    if (input instanceof Date) {
      return input;
    }

    if (typeof input === 'string') {
      const match = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (match) {
        const year = Number(match[1]);
        const monthIndex = Number(match[2]) - 1;
        const day = Number(match[3]);
        return new Date(year, monthIndex, day, 0, 0, 0, 0);
      }
    }

    return new Date(input as string | number);
  }

  private resolveFixedHomeDateContext(
    menuType: 'home' | 'campaign'
  ): CampaignDetail | null {
    if (menuType !== 'home') {
      return null;
    }
    return this.getActiveFixedDateContext(this.menu());
  }

  private getActiveFixedDateContext(menu: Menu | null | undefined): CampaignDetail | null {
    if (!menu) {
      return null;
    }
    if (menu.event?.status === 'active' && menu.event.isdatesFixed) {
      return menu.event;
    }
    if (menu.campaign?.status === 'active' && menu.campaign.isdatesFixed) {
      return menu.campaign;
    }
    return null;
  }

  private getFixedPickupDates(context: CampaignDetail): Date[] {
    if (!context.isdatesFixed || !Array.isArray(context.pickup)) {
      return [];
    }

    return context.pickup
      .map((entry) => this.parseCampaignLocalDate(entry.date))
      .filter((date) => !Number.isNaN(date.getTime()))
      .map((date) => this.startOfDay(date));
  }

  getServingDeviationDateKeys(
    servingMethod: 'pickup' | 'delivery'
  ): Set<number> {
    return new Set(
      this.getDeviationDisabledDatesForServingMethod(servingMethod).map((date) =>
        this.normalizeDate(date)
      )
    );
  }

  private isDateInDeviationKeys(date: Date, deviationDateKeys: Set<number>): boolean {
    return deviationDateKeys.has(this.normalizeDate(this.startOfDay(date)));
  }

  private findServingDeviationContaining(
    date: Date,
    deviations: ServingDeviation[]
  ): ServingDeviation | null {
    const checkTime = this.normalizeDate(this.startOfDay(date));

    return (
      deviations.find((deviation) => {
        if (!deviation.startDate || !deviation.endDate) {
          return false;
        }
        const start = this.normalizeDate(this.startOfDay(new Date(deviation.startDate)));
        const end = this.normalizeDate(this.startOfDay(new Date(deviation.endDate)));
        return checkTime >= start && checkTime <= end;
      }) ?? null
    );
  }

  /** Jump to the day after the deviation range that contains `date` (not day-by-day). */
  private movePastServingDeviationRanges(
    date: Date,
    deviations: ServingDeviation[]
  ): Date {
    let result = this.startOfDay(new Date(date));

    while (true) {
      const blocking = this.findServingDeviationContaining(result, deviations);
      if (!blocking?.endDate) {
        return result;
      }

      const dayAfterDeviation = this.startOfDay(new Date(blocking.endDate));
      dayAfterDeviation.setDate(dayAfterDeviation.getDate() + 1);
      result = dayAfterDeviation;
    }
  }

  private buildDisabledDatesForFixedRange(
    rangeStart: Date,
    rangeEnd: Date,
    allowedDates: Date[],
    today: Date,
    deviationDateKeys: Set<number>
  ): Date[] {
    const disabledDates: Date[] = [];
    const current = this.startOfDay(new Date(rangeStart));

    while (current <= rangeEnd) {
      const dateToCheck = this.startOfDay(new Date(current));
      const isPast = dateToCheck < today;
      const isAllowed = allowedDates.some(
        (allowed) => this.normalizeDate(allowed) === this.normalizeDate(dateToCheck)
      );
      const isDeviation = this.isDateInDeviationKeys(dateToCheck, deviationDateKeys);

      if (isPast || !isAllowed || isDeviation) {
        disabledDates.push(new Date(dateToCheck));
      }

      current.setDate(current.getDate() + 1);
    }

    return disabledDates;
  }

  private startOfDay(date: Date): Date {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }

}
